import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Icon } from "rhombus-react";

import { noop, isDynamic } from "utils/common";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import useQueryParams from "hooks/useQueryParams";
import { getManualClassificationEnabled } from "selectors/Intelligence";

import BladeActions from "widgets/BladeActions";
import { VIDEO_AD_TYPES } from "utils/constants";

const SHOW_LABELS_ACTION = {
	shouldDisplay({ isManualClassificationEnabled }){
		return isManualClassificationEnabled;
	},
	component: () => {
		const { getParam, setParam } = useQueryParams();
		const isCreativeDynamic = useSelector(state => isDynamic(state.creativeDetails));

		const showLabels = getParam("showLabels", "true");

		function toggle(){
			setParam("showLabels", showLabels ? "false" : "true", true);
		}

		if(showLabels){
			return (
				<div onClick={toggle}>
					<Icon
						name="fas fa-tag active"
						data-tip="Creative Labels"
						data-place="left"
					/>
				</div>
			);
		}

		if(isCreativeDynamic){
			return (
				<Icon
					name="far fa-tag disabled"
					data-tip="Creative labels must be applied to versions for dynamic creatives."
					data-place="left"
				/>
			);
		}
		return (
			<div onClick={toggle}>
				<Icon
					name="far fa-tag"
					data-tip="Creative Labels"
					data-place="left"
				/>
			</div>
		);
	},
};

const REPLAY_ACTION = {
	shouldDisplay: context => context.onReplay,
	component: (context) => {
		return (
			<div
				onClick={context.onReplay || noop}
			>
				<Icon name="far fa-sync-alt" data-tip="Replay" data-place="left" />
			</div>
		);
	},
};

const EXTERNAL_PREVIEW_ACTION = {
	shouldDisplay: context => context.previewURL,
	component: (context) => {
		const isLoaded = useSelector(state => state.creativeDetails.isLoaded);
		if(isLoaded){
			return (
				<a href={context.previewURL} target="_blank">
					<Icon
						name="far fa-external-link-alt"
						data-tip="Preview Creative"
						data-place="left"
					/>
				</a>
			);
		}
		return (
			<Icon
				name="far fa-external-link-alt disabled"
				data-tip="Preview Creative"
				data-place="left"
			/>
		);
	},
};

const MUTE_ACTION = {
	shouldDisplay: (context) => {
		return VIDEO_AD_TYPES.includes(context?.adType) || context.isCarouselVideo;
	},
	component: (context) => {
		const { muted, setIsMuted } = context;
		if (muted) {
			return (
				<div onClick={() => setIsMuted(false)}>
					<Icon
						name="far fa-volume-mute"
						data-tip="Unmute"
						data-place="left"
					/>
				</div>
			);
		}

		return (
			<div onClick={() => setIsMuted(true)}>
				<Icon
					name="far fa-volume"
					data-tip="Mute"
					data-place="left"
				/>
			</div>
		);
	},
};

export default function CreativePreviewBladeToolbar(props){
	useTooltipRebuild();
	const isManualClassificationEnabled = useSelector(getManualClassificationEnabled);
	const actionProps = {
		actionLists: [
			[SHOW_LABELS_ACTION],
			[REPLAY_ACTION],
			[EXTERNAL_PREVIEW_ACTION, MUTE_ACTION],
		],
		actionContext: {
			isManualClassificationEnabled,
			...props,
		},
	};

	return <BladeActions {...actionProps} />;
}

CreativePreviewBladeToolbar.displayName = "CreativePreviewBladeToolbar";
CreativePreviewBladeToolbar.propTypes = {
	previewURL: PropTypes.string,
	onReplay: PropTypes.func,
};
